import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Khoá đánh dấu "đã thử reload cho URL này rồi" — chống lặp vô hạn khi chunk
 * thật sự không tồn tại. Dùng sessionStorage để chỉ có hiệu lực trong tab hiện tại.
 */
const RELOAD_FLAG = 'chicilon_chunk_reload_for';

/**
 * Nhận diện lỗi không tải được lazy chunk.
 *
 * Khi một tab mở lâu còn giữ `index.html` cũ, nó sẽ xin file chunk theo hash cũ.
 * Sau khi deploy, file đó không còn trên server; `app-nginx.conf` lại có
 * `try_files $uri $uri/ /index.html` nên nginx trả về `index.html` (200, text/html)
 * thay vì 404 → trình duyệt từ chối vì sai MIME type.
 *
 * Thông điệp lỗi khác nhau giữa các trình duyệt nên cần khớp nhiều biến thể.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const message =
    typeof error === 'string'
      ? error
      : `${(error as Error)?.name ?? ''} ${(error as Error)?.message ?? ''}`;

  return (
    /failed to fetch dynamically imported module/i.test(message) || // Chrome / Edge
    /error loading dynamically imported module/i.test(message) || // Firefox
    /importing a module script failed/i.test(message) || // Safari
    /chunkloaderror/i.test(message) ||
    /loading chunk \S+ failed/i.test(message) ||
    /expected a javascript/i.test(message) // lỗi MIME text/html
  );
}

@Injectable({ providedIn: 'root' })
export class ChunkErrorRecoveryService {
  private readonly router = inject(Router);

  /**
   * URL mà router đang cố điều hướng tới.
   *
   * Cần thiết vì khi navigation thất bại thì thanh địa chỉ VẪN GIỮ URL cũ.
   * Ví dụ: đang ở `/login`, bấm đăng nhập, router đi tới `/crm` rồi fail —
   * lúc đó `window.location.pathname` vẫn là `/login`. Nếu lấy giá trị đó làm
   * đích để tải lại thì người dùng bị đưa về đúng trang login và phải đăng
   * nhập thêm lần nữa.
   */
  private lastAttemptedUrl: string | null = null;

  /** Chặn việc cả NavigationError lẫn ErrorHandler cùng gọi tải lại cho một lỗi. */
  private recovering = false;

  init(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(event => {
      this.lastAttemptedUrl = event.url;
    });

    this.router.events.pipe(filter(event => event instanceof NavigationError)).subscribe(event => {
      if (isChunkLoadError(event.error)) {
        this.recover(event.url);
      }
    });

    // Điều hướng thành công → phiên bản app đã đồng bộ, bỏ cờ đi.
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.lastAttemptedUrl = null;
      this.recovering = false;
      this.clearFlag();
    });
  }

  /**
   * Tải lại app từ server để lấy `index.html` mới (trỏ tới hash chunk mới),
   * rồi đi tiếp tới đúng URL người dùng đang muốn vào.
   *
   * Chỉ làm đúng một lần cho mỗi URL: nếu sau khi reload vẫn lỗi thì
   * nguyên nhân không phải chunk cũ, reload thêm cũng vô ích.
   *
   * Bỏ trống `targetUrl` khi nơi gọi không biết đích (ErrorHandler toàn cục);
   * khi đó service tự lấy URL mà router đang cố tới.
   */
  recover(targetUrl?: string): void {
    // Một lỗi chunk thường đến từ CẢ NavigationError lẫn ErrorHandler. Khoá ngay
    // từ lần gọi đầu, kể cả khi lần đó quyết định dừng — nếu không, lần gọi thứ
    // hai sẽ đi tiếp và reload, tạo vòng lặp vô hạn.
    if (this.recovering) return;
    this.recovering = true;

    const target = targetUrl || this.lastAttemptedUrl || window.location.pathname + window.location.search;

    if (this.readFlag() === target) {
      console.error(
        `[chunk-recovery] Vẫn không tải được chunk cho "${target}" sau khi đã reload. Dừng để tránh lặp vô hạn.`
      );
      // Giữ nguyên cờ: người dùng bấm F5 cũng không bị reload lặp lại.
      // Cờ chỉ được xoá khi có một lần điều hướng thành công (NavigationEnd).
      return;
    }

    this.writeFlag(target);
    console.warn(`[chunk-recovery] Chunk cũ không còn trên server. Đang tải lại app để vào "${target}".`);

    // location.assign (không phải router.navigate) để buộc trình duyệt lấy
    // index.html mới từ server thay vì dùng lại bundle cũ trong bộ nhớ.
    window.location.assign(target);
  }

  // sessionStorage có thể ném lỗi ở chế độ ẩn danh / bị chặn cookie.
  private readFlag(): string | null {
    try {
      return sessionStorage.getItem(RELOAD_FLAG);
    } catch {
      return null;
    }
  }

  private writeFlag(value: string): void {
    try {
      sessionStorage.setItem(RELOAD_FLAG, value);
    } catch {
      /* bỏ qua: không có sessionStorage thì mất khả năng chống lặp, chấp nhận được */
    }
  }

  private clearFlag(): void {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* bỏ qua */
    }
  }
}
